import React from "react";

/**
 * Shared presentation primitives for the Profile screens.
 *
 * These mirror the Employee Details design language — compact bordered cards
 * with uppercase headers and label-left / value-right rows — so every profile
 * tab shares one consistent, enterprise-grade look. Presentation only; they
 * hold no state and trigger no side effects.
 */

// Label/value row: fixed-width muted label on the left, emphasised value on the
// right. Falls back to an em dash when the value is empty.
export function InfoRow({
  label,
  value,
}: Readonly<{
  label: string;
  value?: React.ReactNode;
}>) {
  return (
    <div className="flex justify-between items-center gap-4 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-xs text-gray-500 dark:text-gray-400 w-36 shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 text-right break-words">
        {value || "—"}
      </span>
    </div>
  );
}

export function InfoField({
  label,
  value,
}: Readonly<{
  label: string;
  value?: React.ReactNode;
}>) {
  return (
    <div className="flex flex-col min-w-0 px-4 py-1.5 border-b border-gray-100 dark:border-gray-700">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 break-words">
        {value || "—"}
      </span>
    </div>
  );
}

export function EditButton({
  onClick,
  label = "Edit",
}: Readonly<{ onClick: () => void; label?: string }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-xs px-4 py-2 transition-colors"
    >
      {label}
    </button>
  );
}

export function EmptyState({
  message,
  children,
}: Readonly<{ message: string; children?: React.ReactNode }>) {
  return (
    <div className="md:h-full md:[container-type:size]">
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-8 text-center [@container(max-height:150px)]:py-3 [@container(max-height:110px)]:flex-row">
        <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
        {children}
      </div>
    </div>
  );
}

export const DENSE_FIELD_GRID_CLASS = "lg:[@media(max-height:820px)]:grid-cols-3";

export function InfoFieldGrid({
  children,
  className,
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div className="overflow-hidden">
      <div className={`grid grid-cols-1 sm:grid-cols-2 -mb-px ${className ?? ""}`}>{children}</div>
    </div>
  );
}


// Bordered card with an uppercase header. `action` renders an optional control
// (e.g. an Edit button) on the right of the header.
export function InfoCard({
  title,
  action,
  children,
  className,
}: Readonly<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <div className={`flex flex-col min-h-0 min-w-0 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 overflow-hidden shadow-sm ${className ?? ""}`}>
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
          {title}
        </h3>

        {action}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto bg-white dark:bg-gray-800 pb-1">{children}</div>
    </div>
  );
}

// Centered empty-state used when a section has no data.
export function SectionEmpty({ message }: Readonly<{ message: string }>) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6M5 8h14M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  );
}
